import React, { useState } from 'react';
import LiveCallMonitor from "../components/voice/LiveCallMonitor";
import LiveChatMonitor from "../components/chat/LiveChatMonitor";
import UnifiedAnalytics from "../components/analytics/UnifiedAnalytics";
import { MessageSquare, Phone, BarChart3, ArrowRight } from "lucide-react";

// --- COLORFUL NAVIGATION CARDS (Quick Actions Style) ---

const NavCard = ({ active, onClick, icon: Icon, title, description, colorClass, bgClass, activeBorderClass }) => (
    <button
        onClick={onClick}
        className={`
            relative overflow-hidden text-left p-5 rounded-xl border transition-all duration-200 group w-full h-full flex flex-col justify-between
            ${active
                ? `bg-white shadow-md ring-1 ${activeBorderClass} border-transparent`
                : 'bg-white border-slate-200 hover:border-slate-300 hover:shadow-sm'
            }
        `}
    >
        {/* Background Accent */}
        <div className={`absolute top-0 right-0 w-24 h-24 -mr-8 -mt-8 rounded-full opacity-10 transition-transform group-hover:scale-110 ${bgClass}`} />

        <div className="relative z-10">
            <div className={`p-3 rounded-lg w-fit mb-3 ${bgClass} bg-opacity-10`}>
                <Icon className={`w-6 h-6 ${colorClass}`} />
            </div>
            <h3 className={`text-base font-bold mb-1 ${active ? 'text-slate-900' : 'text-slate-700'}`}>
                {title}
            </h3>
            <p className="text-xs text-slate-500 font-medium">
                {description}
            </p>
        </div>

        {active && (
            <div className={`absolute bottom-0 left-0 h-1 w-full ${bgClass}`} />
        )}
    </button>
);

const LiveMonitorPage = () => {
    const [activeTab, setActiveTab] = useState('chat');

    return (
        <div className="min-h-screen bg-slate-50 p-6 transition-colors duration-500">
            <div className="max-w-[1600px] mx-auto space-y-8">

                {/* Header Section */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold tracking-tight text-slate-900">Live Monitor</h1>
                        <p className="text-slate-500 text-sm mt-1">Real-time oversight for Voice & Chat agents</p>
                    </div>

                    {/* System Status Badge */}
                    <div className="flex items-center gap-2">
                        <div className="flex items-center gap-1.5 px-3 py-1 bg-emerald-50 text-emerald-700 rounded-full text-xs font-medium border border-emerald-200">
                            <span className="relative flex h-2 w-2">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                            </span>
                            System Operational
                        </div>
                    </div>
                </div>

                {/* Colorful Navigation Cards (Quick Actions Style) */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <NavCard
                        active={activeTab === 'chat'}
                        onClick={() => setActiveTab('chat')}
                        icon={MessageSquare}
                        title="Live Chat"
                        description="Monitor active conversations"
                        colorClass="text-blue-600"
                        bgClass="bg-blue-600"
                        activeBorderClass="ring-blue-500"
                    />
                    <NavCard
                        active={activeTab === 'voice'}
                        onClick={() => setActiveTab('voice')}
                        icon={Phone}
                        title="Voice AI"
                        description="Listen to active calls"
                        colorClass="text-purple-600"
                        bgClass="bg-purple-600"
                        activeBorderClass="ring-purple-500"
                    />
                    <NavCard
                        active={activeTab === 'analytics'}
                        onClick={() => setActiveTab('analytics')}
                        icon={BarChart3}
                        title="Unified Analytics"
                        description="Voice, Chat & Calendar Stats"
                        colorClass="text-orange-600"
                        bgClass="bg-orange-600"
                        activeBorderClass="ring-orange-500"
                    />
                </div>

                {/* Tab Content Area */}
                <div className="animate-in fade-in zoom-in-95 duration-300 min-h-[600px]">
                    {activeTab === 'chat' && <LiveChatMonitor />}
                    {activeTab === 'voice' && (
                        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden min-h-[600px] flex flex-col">
                            {/* Wrapper to give Voice Monitor a clean container matching others */}
                            {/* FIX: Must pass profileId="default" otherwise hook hangs on loading */}
                            <LiveCallMonitor profileId="default" />
                        </div>
                    )}
                    {activeTab === 'analytics' && <UnifiedAnalytics />}
                </div>
            </div>
        </div>
    );
};

export default LiveMonitorPage;
